import { useState, useMemo, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import AnnouncementModal from './AnnouncementModal'
import SearchFilter from './SearchFilter'
import { Calendar, ArrowRight, Bell, FileText, AlertCircle } from 'lucide-react'

const Announcements = () => {
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [announcements, setAnnouncements] = useState([])

  useEffect(() => {
    fetch('/data/announcements.json?nocache=' + Date.now())
      .then(r => r.json())
      .then(setAnnouncements)
      .catch(err => console.error('Duyurular yüklenemedi:', err))
  }, [])

  const categories = useMemo(() => [...new Set(announcements.map(a => a.category))], [announcements])

  const filteredAnnouncements = useMemo(() => {
    return announcements.filter(a => {
      const s = searchTerm.toLowerCase()
      const matchesSearch = !s || a.title.toLowerCase().includes(s) || a.excerpt.toLowerCase().includes(s)
      const matchesCategory = !selectedCategory || a.category === selectedCategory
      return matchesSearch && matchesCategory
    })
  }, [announcements, searchTerm, selectedCategory])

  const handleAnnouncementClick = (a) => { setSelectedAnnouncement(a); setIsModalOpen(true) }
  const handleCloseModal = () => { setIsModalOpen(false); setSelectedAnnouncement(null) }

  const getTypeIcon = (type) => {
    switch (type) {
      case 'important': return <AlertCircle size={16} className="text-red-500" />
      case 'update': return <Bell size={16} className="text-blue-500" />
      case 'news': return <FileText size={16} className="text-green-500" />
      default: return <FileText size={16} className="text-gray-500" />
    }
  }
  const getTypeBadge = (type) => ({ important:"destructive", update:"default", news:"secondary", info:"outline" }[type] || "outline")

  return (
    <section id="announcements" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Başlık alanı */}
        <div className="relative overflow-hidden rounded-3xl mb-10 border bg-white/60 backdrop-blur supports-[backdrop-filter]:bg-white/40">
          <div className="absolute inset-0 bg-[radial-gradient(800px_200px_at_10%_-10%,#60a5fa30,transparent),radial-gradient(600px_200px_at_90%_120%,#34d39930,transparent)]" />
          <div className="relative px-6 py-12 text-center">
            <h2 className="text-4xl font-bold text-gray-900 tracking-tight">Duyurular</h2>
            <p className="mt-3 text-gray-600 text-lg">Mevzuat, şirket haberleri ve önemli bilgilere hızlı erişim.</p>
            <div className="mt-5 inline-flex gap-2">
              {categories.slice(0,6).map(c =>
                <span key={c} className="text-xs rounded-full border px-3 py-1 bg-white/70">{c}</span>
              )}
            </div>
          </div>
        </div>

        {/* Arama & Filtre */}
        <div className="mb-8">
          <SearchFilter onSearch={setSearchTerm} onFilter={setSelectedCategory} categories={categories} />
        </div>

        {/* Öne çıkan (beyaz kart) */}
        {filteredAnnouncements[0] && (
          <div className="mb-12">
            <Card className="group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 rounded-2xl cursor-pointer border"
                  onClick={() => handleAnnouncementClick(filteredAnnouncements[0])}>
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(filteredAnnouncements[0].type)}
                    <Badge variant={getTypeBadge(filteredAnnouncements[0].type)} className="text-xs">Öne Çıkan</Badge>
                  </div>
                  <div className="flex items-center text-gray-500 text-sm">
                    <Calendar size={14} className="mr-1" />{filteredAnnouncements[0].date}
                  </div>
                </div>
                <CardTitle className="text-xl text-gray-900 group-hover:text-blue-600 transition-colors">
                  {filteredAnnouncements[0].title}
                </CardTitle>
                <CardDescription className="text-gray-600 mt-2 line-clamp-3">
                  {filteredAnnouncements[0].excerpt}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 p-0">
                  Oku <ArrowRight size={14} className="ml-1" />
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Diğer duyurular */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredAnnouncements.slice(1).length > 0 ? (
            filteredAnnouncements.slice(1).map((a) => (
              <Card key={a.id} className="group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 rounded-2xl cursor-pointer"
                    onClick={() => handleAnnouncementClick(a)}>
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(a.type)}
                      <Badge variant={getTypeBadge(a.type)} className="text-xs">{a.category}</Badge>
                    </div>
                    <span className="text-xs text-gray-500">{a.readTime}</span>
                  </div>
                  <CardTitle className="text-lg text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">{a.title}</CardTitle>
                  <CardDescription className="text-sm text-gray-600 line-clamp-3">{a.excerpt}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar size={14} className="mr-1" />{a.date}
                    </div>
                    <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 p-0">
                      Oku <ArrowRight size={14} className="ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center py-14">
              <div className="text-gray-600 text-lg mb-2">Kriterlere uygun duyuru yok</div>
              <p className="text-gray-400">Aramayı değiştir veya filtreleri temizle</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <AnnouncementModal
        announcement={selectedAnnouncement}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </section>
  )
}

export default Announcements
